/**
 * An on-device trace of the online sync path, enabled by putting `netdbg=1` in
 * the URL.
 *
 * It exists for one question that nothing else in this repo can answer: when a
 * client is behind the table, is the snapshot arriving late, or arriving on
 * time and not being shown? Those have completely different fixes — the first
 * is transport (see `forcedLongPolling` in firebase.ts), the second is the
 * presentation pipeline in OnlinePlayScreen — and from the outside they look
 * identical: a board that is a move stale.
 *
 * The emulator stack can't tell you either, because the intermediaries that
 * cause the transport half of this only exist between a real handset and real
 * Firestore. And the handset in question was an iPhone paired to Windows
 * machines, where remote-debugging Safari isn't available. So the trace has to
 * be readable on the device itself, like `perf=1` before it.
 *
 * What to read. Each line is one event, timestamped from the previous one:
 *
 *   MOVE  this client submitted a move; the POST has returned, so the server
 *         has already committed and published it
 *   SNAP  a Firestore snapshot arrived — its version, its event seq, how many
 *         events it carried, and `cache` if the SDK answered offline
 *   SHOW  the board advanced its displayed state to that version
 *   ERR   the listener failed (it re-attaches with backoff underneath)
 *
 * The tell is the gap between MOVE and the SNAP carrying its version:
 *
 *   - seconds between MOVE and SNAP → transport. The document was written the
 *     moment the POST returned; the bytes took that long to arrive. Compare
 *     against a desktop on the same table, which should be well under a second.
 *   - SNAP prompt but no SHOW, or SHOW seconds later → presentation. The data
 *     was here; something in the animation pipeline held it.
 *
 * `lp` reports whether long-polling was forced for this client, so the two
 * transports can be compared on the same device by loading with `lp=0` and
 * `lp=1` in turn.
 */

export type NetDiagKind = "MOVE" | "SNAP" | "SHOW" | "ERR";

export interface NetDiagEntry {
  kind: NetDiagKind;
  /** performance.now() at the moment it happened. */
  at: number;
  /** Document version, where the event has one. */
  version?: number;
  seq?: number;
  events?: number;
  fromCache?: boolean;
  detail?: string;
}

/** Enough history to cover several turns, small enough to render every frame. */
const MAX_ENTRIES = 40;

export function netDiagEnabled(): boolean {
  if (typeof window === "undefined") return false;
  // Same whole-URL match as perfHud: HashRouter puts the app's query string
  // after the "#", so the flag can legitimately appear on either side of it.
  return window.location.href.includes("netdbg=1");
}

const entries: NetDiagEntry[] = [];
const listeners = new Set<() => void>();

/**
 * Recording is a no-op unless the flag is on, so call sites can be unconditional
 * and the trace costs nothing in a normal session.
 */
export function recordNetDiag(entry: Omit<NetDiagEntry, "at">): void {
  if (!netDiagEnabled()) return;
  entries.push({ ...entry, at: performance.now() });
  if (entries.length > MAX_ENTRIES) entries.shift();
  // A new array identity every time, because useSyncExternalStore compares by
  // reference and mutating in place would render nothing.
  snapshot = entries.slice();
  for (const l of listeners) l();
}

let snapshot: NetDiagEntry[] = [];

export function subscribeNetDiag(onChange: () => void): () => void {
  listeners.add(onChange);
  return () => listeners.delete(onChange);
}

export function getNetDiagEntries(): NetDiagEntry[] {
  return snapshot;
}
